import React, { useState, useEffect } from "react";
import {
  supabase,
  Scale,
  ControlWeight,
  CalibrationRecord,
  Tables,
} from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import {
  PlusIcon,
  ScaleIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
  CalendarIcon,
  CogIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { exportToCsv } from "../utils/exportCsv";
import { format, parseISO, startOfDay, endOfDay } from "date-fns";
import { nl } from "date-fns/locale";
import { logAuditEvent } from "../utils/auditLog";

interface ScaleWithRecords extends Scale {
  latest_calibration?: CalibrationRecord;
  days_since_calibration?: number;
  calibration_status?: "current" | "overdue";
}

interface CalibrationWithDetails extends CalibrationRecord {
  scale_brand: string;
  scale_model: string;
  scale_e_value: number;
  weight_category: string;
  nominal_weight: number;
  calibrated_by_name: string;
}

const Calibrations: React.FC = () => {
  const { selectedPharmacy, user } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "overview" | "scales" | "weights" | "records"
  >("overview");
  const [scales, setScales] = useState<ScaleWithRecords[]>([]);
  const [controlWeights, setControlWeights] = useState<ControlWeight[]>([]);
  const [calibrationRecords, setCalibrationRecords] = useState<
    CalibrationWithDetails[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [showAddScale, setShowAddScale] = useState(false);
  const [showAddWeight, setShowAddWeight] = useState(false);
  const [showCalibration, setShowCalibration] = useState(false);

  // Records tab date filter
  const today = format(new Date(), "yyyy-MM-dd");
  const [recordDateFrom, setRecordDateFrom] = useState(
    format(new Date(new Date().getFullYear(), 0, 1), "yyyy-MM-dd"), // Jan 1 of current year
  );
  const [recordDateTo, setRecordDateTo] = useState(today);
  const [_selectedScale, setSelectedScale] = useState<Scale | null>(null);
  const [editingScale, setEditingScale] = useState<Scale | null>(null);
  const [showEditScale, setShowEditScale] = useState(false);

  // Form states
  const [newScale, setNewScale] = useState({
    brand: "",
    model: "",
    e_value: 0.001,
    serial_number: "",
    location: "",
    validation_date: "",
  });

  const [editScale, setEditScale] = useState({
    brand: "",
    model: "",
    e_value: 0.001,
    serial_number: "",
    location: "",
    validation_date: "",
  });

  const [newWeight, setNewWeight] = useState({
    category: "f1_500g" as "f1_500g" | "f1_50g",
    nominal_weight: 500.0,
    actual_weight: 500.0,
    certificate_number: "",
    certificate_expiry: "",
  });

  const [calibrationForm, setCalibrationForm] = useState({
    scale_id: "",
    control_weight_id: "",
    measured_weight: 0,
    notes: "",
  });

  useEffect(() => {
    if (selectedPharmacy) {
      fetchData();
    }
  }, [selectedPharmacy]);

  const fetchData = async () => {
    if (!selectedPharmacy) {
      return;
    }

    try {
      setLoading(true);
      await Promise.all([
        fetchScales(),
        fetchControlWeights(),
        fetchCalibrationRecords(),
      ]);
    } catch (error) {
      console.error("Error fetching calibration data:", error);
      toast.error("Failed to load calibration data");
    } finally {
      setLoading(false);
    }
  };

  const fetchScales = async () => {
    if (!selectedPharmacy) {
      return;
    }

    const { data, error } = await supabase
      .from(Tables.SCALES)
      .select("*")
      .eq("pharmacy_id", selectedPharmacy.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching scales:", error);
      return;
    }

    // Get latest calibration for each scale
    const scalesWithRecords: ScaleWithRecords[] = await Promise.all(
      data.map(async (scale) => {
        const { data: latestCalibration } = await supabase
          .from(Tables.CALIBRATION_RECORDS)
          .select("*")
          .eq("scale_id", scale.id)
          .order("calibration_date", { ascending: false })
          .limit(1)
          .single();

        let calibrationStatus: "current" | "overdue" = "overdue";
        let daysSince = 0;

        if (latestCalibration) {
          const lastDate = new Date(latestCalibration.calibration_date);
          const today = new Date();
          daysSince = Math.floor(
            (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24),
          );

          if (daysSince <= 7) {
            calibrationStatus = "current";
          } else {
            calibrationStatus = "overdue";
          }
        }

        return {
          ...scale,
          latest_calibration: latestCalibration,
          days_since_calibration: daysSince,
          calibration_status: calibrationStatus,
        };
      }),
    );

    setScales(scalesWithRecords);
  };

  const fetchControlWeights = async () => {
    if (!selectedPharmacy) {
      return;
    }

    const { data, error } = await supabase
      .from(Tables.CONTROL_WEIGHTS)
      .select("*")
      .eq("pharmacy_id", selectedPharmacy.id)
      .eq("status", "active")
      .order("category");

    if (error) {
      console.error("Error fetching control weights:", error);
      return;
    }

    setControlWeights(data || []);
  };

  const fetchCalibrationRecords = async () => {
    if (!selectedPharmacy) {
      return;
    }

    const { data, error } = await supabase
      .from("calibration_details")
      .select("*")
      .eq("pharmacy_id", selectedPharmacy.id)
      .order("calibration_date", { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error fetching calibration records:", error);
      return;
    }

    setCalibrationRecords(data || []);
  };

  const filteredRecords = calibrationRecords.filter((r) => {
    if (!r.calibration_date) return true;
    const d = parseISO(r.calibration_date);
    return (
      d >= startOfDay(parseISO(recordDateFrom)) &&
      d <= endOfDay(parseISO(recordDateTo))
    );
  });

  const getStatusLabel = (status: string) => {
    if (status === "passed") return "Geslaagd";
    if (status === "needs_adjustment") return "Aanpassing nodig";
    return "Niet geslaagd";
  };

  const handleExportCsv = () => {
    const headers = [
      "Balans",
      "Datum",
      "Testgewicht (g)",
      "Gemeten (g)",
      "Afwijking (g)",
      "Resultaat",
      "Uitgevoerd door",
    ];
    const rows = filteredRecords.map((r) => [
      `${r.scale_brand} ${r.scale_model}`,
      r.calibration_date ? format(parseISO(r.calibration_date), "dd-MM-yyyy") : "",
      r.expected_weight ?? "",
      r.measured_weight ?? "",
      r.deviation ?? "",
      getStatusLabel(r.status),
      r.calibrated_by_name || "",
    ]);
    exportToCsv(
      `kalibraties_${recordDateFrom}_${recordDateTo}.csv`,
      headers,
      rows,
    );
    toast.success("CSV geëxporteerd");
  };

  const handlePrintReport = () => {
    const win = window.open("", "_blank");
    if (!win) return;

    const statusColor = (status: string) => {
      if (status === "passed") return "#16a34a";
      if (status === "needs_adjustment") return "#d97706";
      return "#dc2626";
    };

    const rows = filteredRecords
      .map(
        (r) => `
        <tr>
          <td>${r.calibration_date ? format(parseISO(r.calibration_date), "dd MMM yyyy", { locale: nl }) : "—"}</td>
          <td>${r.scale_brand} ${r.scale_model}</td>
          <td>${r.weight_category === "f1_500g" ? "F1 500g" : "F1 50g"}</td>
          <td class="num">${r.expected_weight ?? "—"}g</td>
          <td class="num">${r.measured_weight ?? "—"}g</td>
          <td class="num" style="color:${r.deviation > 0 ? "#dc2626" : r.deviation < 0 ? "#2563eb" : "#374151"}">${r.deviation > 0 ? "+" : ""}${r.deviation ?? "—"}g</td>
          <td><span style="color:${statusColor(r.status)};font-weight:600">${getStatusLabel(r.status)}</span></td>
          <td>${r.calibrated_by_name || "—"}</td>
        </tr>`,
      )
      .join("");

    const periodLabel = `${format(parseISO(recordDateFrom), "d MMMM yyyy", { locale: nl })} – ${format(parseISO(recordDateTo), "d MMMM yyyy", { locale: nl })}`;

    win.document.write(`<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <title>Kalibratie Rapport – ${selectedPharmacy?.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11pt; color: #1f2937; padding: 24mm 20mm; }
    header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8mm; border-bottom: 2px solid #1d4ed8; padding-bottom: 4mm; }
    header h1 { font-size: 18pt; color: #1d4ed8; }
    header .meta { text-align: right; font-size: 9pt; color: #6b7280; line-height: 1.6; }
    h2 { font-size: 12pt; margin: 6mm 0 3mm; color: #374151; }
    table { width: 100%; border-collapse: collapse; font-size: 9pt; }
    th { background: #eff6ff; color: #1e40af; text-align: left; padding: 5px 8px; border-bottom: 2px solid #bfdbfe; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.05em; }
    td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: middle; }
    tr:last-child td { border-bottom: none; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .summary { margin-top: 6mm; padding: 3mm 4mm; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 9pt; color: #374151; }
    .summary span { font-weight: 700; }
    .signature { margin-top: 16mm; display: flex; gap: 20mm; }
    .sig-block { flex: 1; }
    .sig-line { border-top: 1px solid #374151; margin-top: 12mm; padding-top: 2mm; font-size: 8pt; color: #6b7280; }
    footer { margin-top: 10mm; font-size: 8pt; color: #9ca3af; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 3mm; }
    @media print { body { padding: 10mm; } }
  </style>
</head>
<body>
  <header>
    <div>
      <h1>Kalibratie Rapport</h1>
      <p style="margin-top:2mm;color:#374151">${selectedPharmacy?.name}</p>
      <p style="font-size:9pt;color:#6b7280">${selectedPharmacy?.address || ""}</p>
    </div>
    <div class="meta">
      <div><strong>Periode:</strong> ${periodLabel}</div>
      <div><strong>Aangemaakt op:</strong> ${format(new Date(), "d MMMM yyyy 'om' HH:mm", { locale: nl })}</div>
      <div><strong>Aangemaakt door:</strong> ${user?.full_name || ""}</div>
    </div>
  </header>

  <h2>Kalibratie records (${filteredRecords.length})</h2>

  <table>
    <thead>
      <tr>
        <th>Datum</th>
        <th>Weegschaal</th>
        <th>Controlegewicht</th>
        <th class="num">Verwacht</th>
        <th class="num">Gemeten</th>
        <th class="num">Afwijking</th>
        <th>Resultaat</th>
        <th>Uitgevoerd door</th>
      </tr>
    </thead>
    <tbody>${rows || '<tr><td colspan="8" style="text-align:center;color:#9ca3af;padding:12px">Geen records gevonden in deze periode</td></tr>'}</tbody>
  </table>

  <div class="summary">
    Geslaagd: <span style="color:#16a34a">${filteredRecords.filter((r) => r.status === "passed").length}</span> &nbsp;|&nbsp;
    Aanpassing nodig: <span style="color:#d97706">${filteredRecords.filter((r) => r.status === "needs_adjustment").length}</span> &nbsp;|&nbsp;
    Niet geslaagd: <span style="color:#dc2626">${filteredRecords.filter((r) => r.status === "failed").length}</span>
  </div>

  <div class="signature">
    <div class="sig-block">
      <div class="sig-line">Handtekening verantwoordelijke apotheker</div>
    </div>
    <div class="sig-block">
      <div class="sig-line">Datum &amp; stempel</div>
    </div>
  </div>

  <footer>${selectedPharmacy?.name} &nbsp;·&nbsp; ${selectedPharmacy?.phone || ""} &nbsp;·&nbsp; ${selectedPharmacy?.email || ""}</footer>

  <script>window.onload = () => { window.print(); }</script>
</body>
</html>`);
    win.document.close();
  };

  const handleAddScale = async () => {
    if (!selectedPharmacy || !newScale.brand || !newScale.model) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const scaleData: Partial<Scale> = {
        pharmacy_id: selectedPharmacy.id,
        brand: newScale.brand,
        model: newScale.model,
        e_value: newScale.e_value,
        serial_number: newScale.serial_number,
        location: newScale.location,
      };

      // Only add validation_date if it's provided
      if (newScale.validation_date) {
        // Convert MM/YYYY to YYYY-MM-01
        const [month, year] = newScale.validation_date.split("/");
        scaleData.validation_date = `${year}-${month.padStart(2, "0")}-01`;
      }

      const { error } = await supabase.from(Tables.SCALES).insert(scaleData);

      if (error) {
        console.error("Error adding scale:", error);
        toast.error("Failed to add scale");
        return;
      }

      toast.success("Scale added successfully");
      setShowAddScale(false);
      setNewScale({
        brand: "",
        model: "",
        e_value: 0.001,
        serial_number: "",
        location: "",
        validation_date: "",
      });
      await fetchScales();
    } catch (error) {
      console.error("Error adding scale:", error);
      toast.error("Failed to add scale");
    }
  };

  const handleEditScale = async () => {
    if (!editingScale || !editScale.brand || !editScale.model) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const updateData: Partial<Scale> = {
        brand: editScale.brand,
        model: editScale.model,
        e_value: editScale.e_value,
        serial_number: editScale.serial_number,
        location: editScale.location,
      };

      // Only add validation_date if it's provided
      if (editScale.validation_date) {
        // Convert MM/YYYY to YYYY-MM-01
        const [month, year] = editScale.validation_date.split("/");
        updateData.validation_date = `${year}-${month.padStart(2, "0")}-01`;
      }

      const { error } = await supabase
        .from(Tables.SCALES)
        .update(updateData)
        .eq("id", editingScale.id);

      if (error) {
        console.error("Error updating scale:", error);
        toast.error("Failed to update scale");
        return;
      }

      toast.success("Scale updated successfully");
      setShowEditScale(false);
      setEditingScale(null);
      await fetchScales();
    } catch (error) {
      console.error("Error updating scale:", error);
      toast.error("Failed to update scale");
    }
  };

  const openEditScale = (scale: Scale) => {
    setEditingScale(scale);
    setEditScale({
      brand: scale.brand,
      model: scale.model,
      e_value: scale.e_value,
      serial_number: scale.serial_number || "",
      location: scale.location || "",
      validation_date: scale.validation_date
        ? new Date(scale.validation_date)
            .toLocaleDateString("nl-NL", { month: "2-digit", year: "numeric" })
            .replace("-", "/")
        : "",
    });
    setShowEditScale(true);
  };

  const getValidationAlert = (
    scale: Scale,
  ): {
    show: boolean;
    message: string;
    severity: "warning" | "danger";
  } | null => {
    if (!scale.validation_date) {
      return null;
    }

    const validationDate = new Date(scale.validation_date);
    const today = new Date();
    const monthsUntilValidation =
      (validationDate.getFullYear() - today.getFullYear()) * 12 +
      (validationDate.getMonth() - today.getMonth());

    if (monthsUntilValidation <= 0) {
      return {
        show: true,
        message: "Let op: herijking balans is verlopen!",
        severity: "danger",
      };
    } else if (monthsUntilValidation <= 3) {
      return {
        show: true,
        message: `Let op: herijking balans noodzakelijk binnen ${monthsUntilValidation} maand${monthsUntilValidation === 1 ? "" : "en"}`,
        severity: "warning",
      };
    }
    return null;
  };

  const handleAddWeight = async () => {
    if (!selectedPharmacy) {
      return;
    }

    try {
      const { error } = await supabase.from(Tables.CONTROL_WEIGHTS).insert({
        pharmacy_id: selectedPharmacy.id,
        ...newWeight,
      });

      if (error) {
        console.error("Error adding control weight:", error);
        toast.error("Failed to add control weight");
        return;
      }

      toast.success("Control weight added successfully");
      setShowAddWeight(false);
      setNewWeight({
        category: "f1_500g",
        nominal_weight: 500.0,
        actual_weight: 500.0,
        certificate_number: "",
        certificate_expiry: "",
      });
      await fetchControlWeights();
    } catch (error) {
      console.error("Error adding control weight:", error);
      toast.error("Failed to add control weight");
    }
  };

  const handleCalibration = async () => {
    if (
      !selectedPharmacy ||
      !user ||
      !calibrationForm.scale_id ||
      !calibrationForm.control_weight_id
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Get the control weight to calculate deviation
      const { data: controlWeight } = await supabase
        .from(Tables.CONTROL_WEIGHTS)
        .select("actual_weight")
        .eq("id", calibrationForm.control_weight_id)
        .single();

      if (!controlWeight) {
        toast.error("Control weight not found");
        return;
      }

      // Get scale to determine status based on e-value
      const { data: scale } = await supabase
        .from(Tables.SCALES)
        .select("e_value")
        .eq("id", calibrationForm.scale_id)
        .single();

      if (!scale) {
        toast.error("Scale not found");
        return;
      }

      const deviation =
        calibrationForm.measured_weight - controlWeight.actual_weight;
      let status: "passed" | "failed" | "needs_adjustment";

      if (Math.abs(deviation) <= scale.e_value) {
        status = "passed";
      } else if (Math.abs(deviation) <= 2 * scale.e_value) {
        status = "needs_adjustment";
      } else {
        status = "failed";
      }

      const nextCalibrationDate = new Date();
      nextCalibrationDate.setDate(nextCalibrationDate.getDate() + 7);

      const { error } = await supabase.from(Tables.CALIBRATION_RECORDS).insert({
        pharmacy_id: selectedPharmacy.id,
        scale_id: calibrationForm.scale_id,
        control_weight_id: calibrationForm.control_weight_id,
        calibration_date: new Date().toISOString().split("T")[0],
        calibrated_by: user.id,
        measured_weight: calibrationForm.measured_weight,
        expected_weight: controlWeight.actual_weight,
        deviation: deviation,
        status: status,
        notes: calibrationForm.notes,
        next_calibration_date: nextCalibrationDate.toISOString().split("T")[0],
      });

      if (error) {
        console.error("Error recording calibration:", error);
        toast.error("Failed to record calibration");
        return;
      }

      toast.success(`Calibration recorded - Status: ${status}`);

      const auditScale = scales.find((s) => s.id === calibrationForm.scale_id);
      if (auditScale && user && selectedPharmacy) {
        await logAuditEvent({
          pharmacy_id: selectedPharmacy.id,
          user_id: user.id,
          user_name: user.full_name,
          action: "calibrated",
          entity_type: "calibration",
          entity_id: calibrationForm.scale_id,
          entity_label: `${auditScale.brand} ${auditScale.model}`,
          details: { status, deviation },
        });
      }

      setShowCalibration(false);
      setCalibrationForm({
        scale_id: "",
        control_weight_id: "",
        measured_weight: 0,
        notes: "",
      });
      await fetchData();
    } catch (error) {
      console.error("Error recording calibration:", error);
      toast.error("Failed to record calibration");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "passed":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "needs_adjustment":
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case "failed":
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCalibrationStatusBadge = (
    status: "current" | "overdue",
    daysSince?: number,
  ) => {
    if (daysSince === 7) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Laatste Dag!
        </span>
      );
    }

    switch (status) {
      case "current":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Current
          </span>
        );
      case "overdue":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Niet conform
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Controle van de balansen
        </h1>
        <p className="text-gray-600 mt-1">
          Weegschalen beheer en registratie van de controles
        </p>
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Wettelijke Vereiste:</p>
              <p>
                Elke weegschaal moet minimaal 1x per week worden geijkt. Na 7
                dagen is een weegschaal niet meer conform.
              </p>
            </div>
          </div>
        </div>
        {scales.some((scale) => {
          const alert = getValidationAlert(scale);
          return alert && alert.show;
        }) && (
          <div
            className={`mt-2 p-3 ${
              scales.some((scale) => {
                const alert = getValidationAlert(scale);
                return alert && alert.severity === "danger";
              })
                ? "bg-red-50 border-red-200"
                : "bg-orange-50 border-orange-200"
            } border rounded-md`}
          >
            <div className="flex">
              <ExclamationTriangleIcon
                className={`h-5 w-5 mr-2 ${
                  scales.some((scale) => {
                    const alert = getValidationAlert(scale);
                    return alert && alert.severity === "danger";
                  })
                    ? "text-red-400"
                    : "text-orange-400"
                }`}
              />
              <div
                className={`text-sm ${
                  scales.some((scale) => {
                    const alert = getValidationAlert(scale);
                    return alert && alert.severity === "danger";
                  })
                    ? "text-red-800"
                    : "text-orange-800"
                }`}
              >
                {scales
                  .filter((scale) => {
                    const alert = getValidationAlert(scale);
                    return alert && alert.show;
                  })
                  .map((scale, index) => {
                    const alert = getValidationAlert(scale);
                    return (
                      <p key={scale.id} className={index > 0 ? "mt-1" : ""}>
                        {scale.brand} {scale.model} ({scale.location}):{" "}
                        {alert?.message}
                      </p>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "overview", name: "Overzicht", icon: ScaleIcon },
            { id: "scales", name: "Weegschalen", icon: CogIcon },
            { id: "weights", name: "Controlegewichten", icon: CheckCircleIcon },
            { id: "records", name: "Controlerecords", icon: CalendarIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() =>
                setActiveTab(
                  tab.id as "overview" | "scales" | "weights" | "records",
                )
              }
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <ScaleIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Totaal Weegschalen
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {scales.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Deze Week Geijkt
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {
                      scales.filter((s) => s.calibration_status === "current")
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Laatste Dag conform
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {
                      scales.filter((s) => s.days_since_calibration === 7)
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Niet conform ({">"}7 dagen)
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {
                      scales.filter((s) => s.calibration_status === "overdue")
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Calibration */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Snelle controle
              </h3>
              <button
                onClick={() => setShowCalibration(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Nieuwe controle
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {scales
                .filter((s) => s.calibration_status !== "current")
                .slice(0, 4)
                .map((scale) => (
                  <div
                    key={scale.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {scale.brand} {scale.model}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {scale.location}
                        </p>
                        <p className="text-sm text-gray-500">
                          {scale.days_since_calibration
                            ? `${scale.days_since_calibration} dagen geleden`
                            : "Nog nooit geijkt"}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {scale.calibration_status &&
                          getCalibrationStatusBadge(
                            scale.calibration_status,
                            scale.days_since_calibration,
                          )}
                        <button
                          onClick={() => {
                            setSelectedScale(scale);
                            setCalibrationForm((prev) => ({
                              ...prev,
                              scale_id: scale.id,
                            }));
                            setShowCalibration(true);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Controleer Nu
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Scales Tab */}
      {activeTab === "scales" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Weegschalen Beheer
            </h3>
            <button
              onClick={() => setShowAddScale(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Weegschaal Toevoegen
            </button>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Weegschaal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-waarde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locatie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Laatste IJking
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Herijking
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Acties</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scales.map((scale) => (
                  <tr key={scale.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {scale.brand} {scale.model}
                        </div>
                        <div className="text-sm text-gray-500">
                          S/N: {scale.serial_number}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {scale.e_value}g
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {scale.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {scale.calibration_status &&
                        getCalibrationStatusBadge(
                          scale.calibration_status,
                          scale.days_since_calibration,
                        )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {scale.latest_calibration
                        ? new Date(
                            scale.latest_calibration.calibration_date,
                          ).toLocaleDateString()
                        : "Nooit"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {scale.validation_date ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {new Date(scale.validation_date).toLocaleDateString(
                              "nl-NL",
                              { month: "2-digit", year: "numeric" },
                            )}
                          </div>
                          {(() => {
                            const alert = getValidationAlert(scale);
                            return alert && alert.show ? (
                              <div
                                className={`text-xs mt-1 ${
                                  alert.severity === "danger"
                                    ? "text-red-600 font-medium"
                                    : "text-orange-600"
                                }`}
                              >
                                {alert.severity === "danger"
                                  ? "Verlopen!"
                                  : `${(() => {
                                      const validationDate = new Date(
                                        scale.validation_date || "",
                                      );
                                      const today = new Date();
                                      const monthsUntil =
                                        (validationDate.getFullYear() -
                                          today.getFullYear()) *
                                          12 +
                                        (validationDate.getMonth() -
                                          today.getMonth());
                                      return `${monthsUntil} maand${
                                        monthsUntil === 1 ? "" : "en"
                                      }`;
                                    })()}`}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditScale(scale)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Control Weights Tab */}
      {activeTab === "weights" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Controlegewichten Beheer
            </h3>
            <button
              onClick={() => setShowAddWeight(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Controlegewicht Toevoegen
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {controlWeights.map((weight) => (
              <div
                key={weight.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {weight.category === "f1_500g" ? "F1 500g" : "F1 50g"}{" "}
                      controlegewicht
                    </h4>
                    <p className="text-sm text-gray-500">
                      Nominaal: {weight.nominal_weight}g
                    </p>
                    <p className="text-sm text-gray-500">
                      Werkelijk: {weight.actual_weight}g
                    </p>
                    {weight.certificate_number && (
                      <p className="text-sm text-gray-500">
                        Certificaat: {weight.certificate_number}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button className="text-primary-600 hover:text-primary-900">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Records Tab */}
      {activeTab === "records" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-lg font-medium text-gray-900">
              Controle records
            </h3>
            <div className="flex flex-wrap items-end gap-2">
              {/* Date range filter */}
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Van</label>
                <input
                  type="date"
                  value={recordDateFrom}
                  max={recordDateTo}
                  onChange={(e) => setRecordDateFrom(e.target.value)}
                  className="border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500 py-1.5 px-2"
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Tot</label>
                <input
                  type="date"
                  value={recordDateTo}
                  min={recordDateFrom}
                  max={today}
                  onChange={(e) => setRecordDateTo(e.target.value)}
                  className="border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500 py-1.5 px-2"
                />
              </div>
              <button
                onClick={handlePrintReport}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="Print kalibratie rapport"
              >
                <PrinterIcon className="-ml-1 mr-2 h-4 w-4" />
                Print rapport
              </button>
              <button
                onClick={handleExportCsv}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="Exporteer kalibratie records naar CSV"
              >
                <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
                CSV
              </button>
            </div>
          </div>
          {filteredRecords.length !== calibrationRecords.length && (
            <p className="text-xs text-gray-500">
              {filteredRecords.length} van {calibrationRecords.length} records in geselecteerde periode
            </p>
          )}

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Weegschaal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    controlegewicht
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gemeten
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Afwijking
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Door
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(record.calibration_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {record.scale_brand} {record.scale_model}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.weight_category === "f1_500g"
                        ? "F1 500g"
                        : "F1 50g"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.measured_weight}g
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.deviation > 0 ? "+" : ""}
                      {record.deviation}g
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(record.status)}
                        <span className="ml-2 capitalize">{record.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.calibrated_by_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Scale Modal */}
      {showAddScale && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Weegschaal Toevoegen
                  </h3>
                  <button
                    onClick={() => setShowAddScale(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Merk *
                    </label>
                    <input
                      type="text"
                      value={newScale.brand}
                      onChange={(e) =>
                        setNewScale((prev) => ({
                          ...prev,
                          brand: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="bijv. Mettler Toledo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Model *
                    </label>
                    <input
                      type="text"
                      value={newScale.model}
                      onChange={(e) =>
                        setNewScale((prev) => ({
                          ...prev,
                          model: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="bijv. MS-TS"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      E-waarde (gram) *
                    </label>
                    <select
                      value={newScale.e_value}
                      onChange={(e) =>
                        setNewScale((prev) => ({
                          ...prev,
                          e_value: parseFloat(e.target.value),
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value={0.001}>0.001g</option>
                      <option value={0.01}>0.01g</option>
                      <option value={0.1}>0.1g</option>
                      <option value={1}>1g</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Serienummer
                    </label>
                    <input
                      type="text"
                      value={newScale.serial_number}
                      onChange={(e) =>
                        setNewScale((prev) => ({
                          ...prev,
                          serial_number: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Locatie
                    </label>
                    <input
                      type="text"
                      value={newScale.location}
                      onChange={(e) =>
                        setNewScale((prev) => ({
                          ...prev,
                          location: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="bijv. Receptuur, Laboratorium"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAddScale(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleAddScale}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Toevoegen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Control Weight Modal */}
      {showAddWeight && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    IJkgewicht Toevoegen
                  </h3>
                  <button
                    onClick={() => setShowAddWeight(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Categorie *
                    </label>
                    <select
                      value={newWeight.category}
                      onChange={(e) => {
                        const category = e.target.value as "f1_500g" | "f1_50g";
                        setNewWeight((prev) => ({
                          ...prev,
                          category,
                          nominal_weight: category === "f1_500g" ? 500.0 : 50.0,
                          actual_weight: category === "f1_500g" ? 500.0 : 50.0,
                        }));
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="f1_500g">F1 Categorie - 500,00g</option>
                      <option value="f1_50g">F1 Categorie - 50,00g</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Werkelijke Waarde (gram) *
                    </label>
                    <input
                      type="number"
                      step="0.00001"
                      value={newWeight.actual_weight}
                      onChange={(e) =>
                        setNewWeight((prev) => ({
                          ...prev,
                          actual_weight: parseFloat(e.target.value),
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Certificaatnummer
                    </label>
                    <input
                      type="text"
                      value={newWeight.certificate_number}
                      onChange={(e) =>
                        setNewWeight((prev) => ({
                          ...prev,
                          certificate_number: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Certificaat Vervaldatum
                    </label>
                    <input
                      type="date"
                      value={newWeight.certificate_expiry}
                      onChange={(e) =>
                        setNewWeight((prev) => ({
                          ...prev,
                          certificate_expiry: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Herijkingsdatum (MM/JJJJ)
                    </label>
                    <input
                      type="text"
                      placeholder="MM/JJJJ"
                      value={newScale.validation_date}
                      onChange={(e) =>
                        setNewScale({
                          ...newScale,
                          validation_date: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Datum waarop de balans moet worden herijkt door een
                      officiële instantie (elke 4 jaar)
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAddWeight(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleAddWeight}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Toevoegen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calibration Modal */}
      {showCalibration && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Controle Uitvoeren
                  </h3>
                  <button
                    onClick={() => setShowCalibration(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Weegschaal *
                    </label>
                    <select
                      value={calibrationForm.scale_id}
                      onChange={(e) =>
                        setCalibrationForm((prev) => ({
                          ...prev,
                          scale_id: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">Selecteer weegschaal</option>
                      {scales.map((scale) => (
                        <option key={scale.id} value={scale.id}>
                          {scale.brand} {scale.model} ({scale.location})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Controlegewicht *
                    </label>
                    <select
                      value={calibrationForm.control_weight_id}
                      onChange={(e) =>
                        setCalibrationForm((prev) => ({
                          ...prev,
                          control_weight_id: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">Selecteer controlegewicht</option>
                      {controlWeights.map((weight) => (
                        <option key={weight.id} value={weight.id}>
                          {weight.category === "f1_500g" ? "F1 500g" : "F1 50g"}{" "}
                          - {weight.actual_weight}g
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Gemeten Gewicht (gram) *
                    </label>
                    <input
                      type="number"
                      step="0.00001"
                      value={calibrationForm.measured_weight}
                      onChange={(e) =>
                        setCalibrationForm((prev) => ({
                          ...prev,
                          measured_weight: parseFloat(e.target.value),
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="Voer het gewicht in zoals getoond op de weegschaal"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Opmerkingen
                    </label>
                    <textarea
                      rows={3}
                      value={calibrationForm.notes}
                      onChange={(e) =>
                        setCalibrationForm((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="Eventuele opmerkingen..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCalibration(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleCalibration}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Controle Registreren
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Scale Modal */}
      {showEditScale && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Weegschaal Bewerken
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Merk *
                    </label>
                    <input
                      type="text"
                      value={editScale.brand}
                      onChange={(e) =>
                        setEditScale({ ...editScale, brand: e.target.value })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Model *
                    </label>
                    <input
                      type="text"
                      value={editScale.model}
                      onChange={(e) =>
                        setEditScale({ ...editScale, model: e.target.value })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      E-waarde (g) *
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={editScale.e_value}
                      onChange={(e) =>
                        setEditScale({
                          ...editScale,
                          e_value: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Serienummer
                    </label>
                    <input
                      type="text"
                      value={editScale.serial_number}
                      onChange={(e) =>
                        setEditScale({
                          ...editScale,
                          serial_number: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Locatie
                    </label>
                    <input
                      type="text"
                      value={editScale.location}
                      onChange={(e) =>
                        setEditScale({
                          ...editScale,
                          location: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Herijkingsdatum (MM/JJJJ)
                    </label>
                    <input
                      type="text"
                      placeholder="MM/JJJJ"
                      value={editScale.validation_date}
                      onChange={(e) =>
                        setEditScale({
                          ...editScale,
                          validation_date: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Datum waarop de balans moet worden herijkt door een
                      officiële instantie (elke 4 jaar)
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEditScale(false);
                      setEditingScale(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleEditScale}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Opslaan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calibrations;
